import { FunctionComponent, useMemo, useState } from "react";
import MeasurementInput from "../../components/MeasurementInput";
import Button from "../../components/Button";
import { useForm } from "react-hook-form";
import { Weight, Height, HeartRate, BMI, Fat } from '../../assets/svg-icons'
import calculateBMI from "../../utils/calculate-bmi";
import { gql, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Measurement, useCreateMeasurementMutation } from "src/graphql";

interface MeasurementFormProps {
    measurement?: Partial<Measurement>;
    afterUpdate?: () => any;
}

const MeasurementForm: FunctionComponent<MeasurementFormProps> = ({ measurement = {}, afterUpdate }) => {

    const [updatingMeasurement, setUpdatingMeasurement] = useState(false);
    const [updateMeasurement] = useCreateMeasurementMutation({
        onCompleted(data) {
        },
        onError(err) {
            console.error(err);
        }
    })

    const { register, handleSubmit, formState: { errors, isDirty, dirtyFields }, watch, reset } = useForm({
        defaultValues: {
            weight: measurement.weight || 0,
            height: measurement.height || 0,
            heart_rate: measurement.heart_rate || 0,
            fat: measurement.fat || 0,
        }
    });

    const formValues = watch(['height', 'weight']);
    const bmi = useMemo(() => calculateBMI(formValues[0] / 100, formValues[1]), [formValues[0], formValues[1]]);

    const onUpdateMeasurement = async (data: any) => {
        setUpdatingMeasurement(true)


        if (isDirty) {
            const { data: newMeasurementData } = await updateMeasurement({
                variables: {
                    input: data
                }
            })

            reset({
                height: newMeasurementData?.createMeasurement?.height || 0,
                weight: newMeasurementData?.createMeasurement?.weight || 0,
                fat: newMeasurementData?.createMeasurement?.fat || 0,
                heart_rate: newMeasurementData?.createMeasurement?.heart_rate || 0,
            })
        }
        setUpdatingMeasurement(false)

        afterUpdate && await afterUpdate();
    }

    const onInvalid = (e: any) => {
        console.log("Invalid", e)
    }

    const { t } = useTranslation();

    return (
        <form onSubmit={handleSubmit(onUpdateMeasurement, onInvalid)}>
            <article id='measurement'>
                <h1 className='light-orange'>{t("measurements")}</h1>
                <nav>
                    <Button loading={updatingMeasurement} disabled={!isDirty} type='submit' sm fontLight>{t("save_changes")}</Button>
                </nav>
                <div className='flex flex-row gap-5 flex-wrap'>
                    <div className='min-w-[150px]'>
                        <MeasurementInput inputProps={register('weight', { valueAsNumber: true, min: 0, max: 300 })} unit='kg' color='orange' label={t("weight")} icon={() => <img alt='Weight' className='w-7 md:w-8' src={Weight} />} />
                    </div>
                    <div className='min-w-[150px]'>
                        <MeasurementInput inputProps={register('height', { valueAsNumber: true, min: 0, max: 300 })} unit='cm' color='purple' label={t('height')} icon={() => <img alt='Height' className='w-7 md:w-8' src={Height} />} />
                    </div>
                    <div className='min-w-[150px]'>
                        <MeasurementInput inputProps={register('heart_rate', { required: false, valueAsNumber: true, min: 10, max: 300 })} unit='BPM' color='red' label={t("heart_rate")} icon={() => <img alt='HeartRate' className='w-7 md:w-8' src={HeartRate} />} />
                    </div>
                    <div className='min-w-[150px]'>
                        <MeasurementInput readOnly inputProps={{ value: bmi || 0 }} unit='' color='blue' label='BMI' icon={() => <img alt='BMI' className='w-7 md:w-8' src={BMI} />} />
                    </div>
                    <div className='min-w-[150px]'>
                        <MeasurementInput inputProps={register('fat', { required: false, valueAsNumber: true, min: 1, max: 100 })} unit='%' color='cyan' label={t("fat")} icon={() => <img alt='Fat' className='w-7 md:w-8' src={Fat} />} />
                    </div>
                </div>

            </article>
        </form>
    );
}

export default MeasurementForm;